import type {
  PostProductionParameters,
  PostProductionParametersSchema,
  PostProductionSchemaProperty,
} from "@/lib/workspace-api"
import type { TFunction } from "i18next"

export type PostProductionParameterErrors = Record<string, string>

function propertyType(property: PostProductionSchemaProperty): string {
  if (Array.isArray(property.type)) return property.type[0] ?? "string"
  return property.type ?? "string"
}

function isBlank(value: unknown): boolean {
  return value == null || value === ""
}

function validateStringValue(
  value: string,
  property: PostProductionSchemaProperty,
  t: TFunction<"shot">,
): string | null {
  const minLength = property.minLength
  const maxLength = property.maxLength
  const length = Array.from(value).length

  if (minLength != null && length < minLength) {
    return t("postProduction.parameterValidation.belowMinLength", { min: minLength })
  }
  if (maxLength != null && length > maxLength) {
    return t("postProduction.parameterValidation.aboveMaxLength", { max: maxLength })
  }

  if (property.pattern) {
    try {
      if (!new RegExp(property.pattern).test(value)) {
        return t("postProduction.parameterValidation.patternMismatch")
      }
    } catch {
      return t("postProduction.parameterValidation.invalid")
    }
  }

  return null
}

function validateProperty(
  _key: string,
  value: PostProductionParameters[string],
  property: PostProductionSchemaProperty,
  t: TFunction<"shot">,
): string | null {
  const type = propertyType(property)
  const enumValues = Array.isArray(property.enum) ? property.enum : []

  if (enumValues.length > 0) {
    if (!enumValues.map(String).includes(String(value))) {
      return t("postProduction.parameterValidation.invalidEnum")
    }
    if (type === "string" && typeof value === "string") {
      return validateStringValue(value, property, t)
    }
    return null
  }

  if (type === "boolean") {
    return typeof value === "boolean" ? null : t("postProduction.parameterValidation.invalid")
  }

  if (type === "integer") {
    if (typeof value !== "number" || Number.isNaN(value) || !Number.isInteger(value)) {
      return t("postProduction.parameterValidation.mustBeInteger")
    }
  } else if (type === "number") {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return t("postProduction.parameterValidation.mustBeNumber")
    }
  } else if (type === "string") {
    if (typeof value !== "string") {
      return t("postProduction.parameterValidation.invalid")
    }
    return validateStringValue(value, property, t)
  } else {
    return t("postProduction.parameterValidation.invalid")
  }

  const minimum = property.minimum
  const maximum = property.maximum
  if (typeof value === "number") {
    if (minimum != null && value < minimum) {
      return t("postProduction.parameterValidation.belowMinimum", { min: minimum })
    }
    if (maximum != null && value > maximum) {
      return t("postProduction.parameterValidation.aboveMaximum", { max: maximum })
    }
  }

  return null
}

export function validatePostProductionParameters(
  schema: PostProductionParametersSchema,
  parameters: PostProductionParameters,
  t: TFunction<"shot">,
): PostProductionParameterErrors {
  const errors: PostProductionParameterErrors = {}
  const properties = schema.properties ?? {}
  const required = schema.required ?? []

  for (const key of required) {
    if (isBlank(parameters[key])) {
      errors[key] = t("postProduction.parameterValidation.required")
    }
  }

  for (const [key, property] of Object.entries(properties)) {
    if (!(key in parameters)) continue
    const value = parameters[key]
    if (isBlank(value)) continue

    const message = validateProperty(key, value, property, t)
    if (message) errors[key] = message
  }

  return errors
}

export function firstPostProductionParameterErrorKey(
  schema: PostProductionParametersSchema,
  errors: PostProductionParameterErrors,
): string | undefined {
  const properties = schema.properties ?? {}
  return Object.keys(properties).find((key) => errors[key]) ?? Object.keys(errors)[0]
}

export function parsePostProductionParameterServerError(
  message: string,
): { field: string; message: string } | { field?: undefined; message: string } {
  const match = message.match(/(?:Post-production parameter|Invalid post-production parameter) ([a-z_][a-z0-9_]*)/i)
  if (match?.[1]) {
    return { field: match[1], message }
  }

  const missing = message.match(/Missing post-production parameter: ([a-z_][a-z0-9_]*)/i)
  if (missing?.[1]) {
    return { field: missing[1], message }
  }

  return { message }
}

export function postProductionParameterFieldId(key: string) {
  return `post-production-param-${key}`
}

export function scrollToPostProductionParameterField(key: string) {
  document.getElementById(postProductionParameterFieldId(key))?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  })
}

export function defaultsFromPostProductionSchema(schema: PostProductionParametersSchema): PostProductionParameters {
  const properties = schema.properties ?? {}
  const defaults: PostProductionParameters = {}
  for (const [key, property] of Object.entries(properties)) {
    if (property.default === undefined || property.default === null) continue
    defaults[key] = property.default
  }
  return defaults
}
